/**
 * googleTag, analytics consent lifecycle (Google "basic" consent mode).
 *
 * Owner decision (Gary, 28 Sep 2026): **no Google analytics requests until
 * consent.** So Google Tag Manager is NOT in index.html any more; it is
 * injected here, once, only after the visitor grants the Analytics
 * category. Until then nothing is sent to Google at all, no tag load, no
 * cookieless pings, no automatic page views.
 *
 * Producer contract (what GTM can rely on):
 *   1. `gtag('consent','update', {analytics_storage:'granted', ...})` is
 *      queued FIRST,
 *   2. then GTM is loaded (`gtm.js` event),
 *   3. then `consent_update` (analytics_consent: "granted") is pushed, the
 *      consent state is already effective when this event is processed,
 *   4. then exactly one `page_view` for the current page (analytics.js).
 *   While consent is unknown or denied, analytics.js pushes nothing, so no
 *   denied-period events sit in the dataLayer to be replayed on grant.
 *
 * Withdrawal: an initialised Google tag can't be unloaded, so we
 *   - queue `gtag('consent','update', {analytics_storage:'denied'})`, and
 *   - set `window['ga-disable-<measurement id>'] = true` for every GA4
 *     destination GTM routes to. The Google tag checks this flag before
 *     every hit, which also stops automatic enhanced-measurement and
 *     page-hide engagement hits, no reload needed, so form input survives.
 *   analytics.js stops pushing at the same moment. Regrant clears the flags.
 */

import { isMeasurementHostAllowed } from "@/utils/measurementPolicy";
import { readConsent } from "@/utils/consentCookie";

const GTM_ID = "GTM-NTPNDTF5";

// Every GA4 destination the GTM container routes to: production, and the
// staging test property selected by GTM's hostname lookup (GTM v5+).
const GA4_MEASUREMENT_IDS = ["G-RNSD4EGJPR", "G-PVF4D9LWNX"];

let granted = false;
let gtmLoaded = false;

// Capture only consent and an internal-referrer boolean during module load.
// The raw landing query and raw referrer are never retained. This lets the
// first eligible page view distinguish a returning, already-consented landing
// from a first-choice or regrant view, which must remain query-free.
const analyticsConsentAtDocumentLoad = (() => {
  try { return Boolean(readConsent()?.analytics); } catch (_err) { return false; }
})();
const internalReferrerAtDocumentLoad = (() => {
  try {
    const referrer = new URL(document.referrer);
    return isMeasurementHostAllowed({ hostname: referrer.hostname });
  } catch (_err) {
    return false;
  }
})();

// A document referrer is likewise usable only for a returning, already
// consented external landing. A refusal makes it permanently unavailable for
// this document, so regrant cannot turn a referrer observed during refusal
// into attribution data.
let documentReferrerSuppressed = !analyticsConsentAtDocumentLoad || internalReferrerAtDocumentLoad;

export function isAnalyticsGranted() {
  return granted;
}

/** True only for a returning, consented external landing document. */
export function isCampaignPageViewEligible() {
  return analyticsConsentAtDocumentLoad && !internalReferrerAtDocumentLoad;
}

/** True only while this document may expose its external referrer to analytics. */
export function canUseDocumentReferrer() {
  return !documentReferrerSuppressed;
}

/** True only when consent exists and this build recognises the hostname. */
export function canSendAnalytics() {
  return granted && isMeasurementHostAllowed();
}

function setGoogleAnalyticsDisabled(disabled) {
  GA4_MEASUREMENT_IDS.forEach((id) => {
    window[`ga-disable-${id}`] = disabled;
  });
}

function queueConsent(analyticsStorage) {
  if (typeof window.gtag !== "function") return;
  window.gtag("consent", "update", {
    analytics_storage: analyticsStorage,
    // No Marketing category yet, ad signals stay denied.
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
}

// The Google tag reads document.referrer itself for the first hit of a page
// load (GA4's `dr`), independently of the page_view referrer we push. Hide
// it unless this document arrived with stored consent and no later refusal.
// This also hides an internal route that could have been visited while
// analytics was refused.
function hideIneligibleDocumentReferrer() {
  try {
    if (!document.referrer) return;
    if (!canUseDocumentReferrer()) {
      Object.defineProperty(document, "referrer", { configurable: true, get: () => "" });
      return;
    }
    const referrer = new URL(document.referrer);
    if (!isMeasurementHostAllowed({ hostname: referrer.hostname })) return;
    Object.defineProperty(document, "referrer", { configurable: true, get: () => "" });
  } catch (_err) {
    /* unparseable referrer or locked property, nothing to hide */
  }
}

function loadGoogleTagManager() {
  if (gtmLoaded) return;
  // A preview/local/unknown hostname must never fall through to a production
  // destination just because the GTM container happens to be configured.
  if (!isMeasurementHostAllowed()) return;
  // Never during react-snap's prerender crawl, the injected <script> would
  // be serialised into every snapshot.
  if (typeof navigator !== "undefined" && navigator.userAgent.includes("ReactSnap")) return;
  gtmLoaded = true;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(s);
}

/** Grant analytics. Returns true only on a denied/unknown → granted transition. */
export function grantAnalytics() {
  if (granted) return false;
  setGoogleAnalyticsDisabled(false);
  queueConsent("granted");
  granted = true;
  hideIneligibleDocumentReferrer();
  loadGoogleTagManager();
  return true;
}

/** Deny or withdraw analytics. Safe to call when GTM was never loaded. */
export function denyAnalytics() {
  granted = false;
  documentReferrerSuppressed = true;
  setGoogleAnalyticsDisabled(true);
  queueConsent("denied");
}
